import { isDocumentVisible, millisSinceLastUserActivity, startUserActivityTracking } from "@/main/common/PageActivity";
import { MediaAttentionSession } from "@/main/react/discussion/MediaAttentionSession";

/**
 * Did this reader actually LOOK at the post's media?
 *
 * Design: `designs/01-now/2026-08-01-media-consumption-signal-capture-primitives-design.md` §3.4 (the
 * attention predicate) and §4 P1 (`attentiveMs`), P2 (`itemsSeen`), P4 (`firstAttentionMs`). Slice
 * `MD-2a` of the round-5 work order; the transport is `ClientFactory.recordMediaAttention` (`MD-1b`),
 * the row is `discussion_media_attention` (`MD-1a`).
 *
 * ## The predicate, and where each clause is decided
 *
 * A media item is **attentive** during any interval in which all of these hold:
 *
 * 1. the page is visible (`document.visibilityState === "visible"`);
 * 2. the reader is not idle: `millisSinceLastUserActivity() < IDLE_CUTOFF_MS`. §3.5's correction —
 *    visibility is not attention. A foreground tab on a second monitor is "visible" all day;
 * 3. the item is viewable: at least half of it is inside the viewport, OR it covers at least half of
 *    the viewport. The disjunction is geometric, not conventional (§3.4): a portrait photo taller than
 *    the viewport can never reach 50 % of its own area on screen, and a single-clause rule would
 *    report zero attention for exactly the images the product promotes. Both clauses come from one
 *    `IntersectionObserverEntry`.
 *
 * Design §3.4 lists a fourth clause, a client-side `isLikelyAutomatedClient()` skip. It is
 * deliberately absent: round-5 decision A5 settled "bots classified by `traffic_type`, never
 * client-dropped", and `PageViewTracker.startViewTracking`'s javadoc records why the same skip was
 * removed from the view beacon on 2026-08-20. Classify, never drop: the server labels the row.
 *
 * ## Integration, and why there is a tick as well as the observer
 *
 * Time is integrated between accounting points: every observer callback, every visibility change and a
 * coarse `ACCOUNTING_TICK_MS` timer. No rAF loop and no `scroll` handler, per §3.4. The timer is not
 * decoration: with callbacks alone, a reader who stops moving for ten minutes and then scrolls would
 * have the whole idle stretch credited at that scroll, because the activity that produced the callback
 * is also the activity that resets the idle clock. The tick bounds that error to one tick.
 *
 * ## Raw, per item, summed
 *
 * `attentiveMs` is a raw sum over items and `itemsSeen` its denominator (§4 P1's trap: eight images
 * mechanically out-earn one; the ratio is derived at read time, never stored). There is no completion
 * floor and no quartile (§3.6). `firstAttentionMs` is the first instant any item became attentive,
 * measured from the document's time origin.
 *
 * ## It degrades to silence, never to a false row
 *
 * No `IntersectionObserver`, no body node, no media in the body, or a transport failure all produce no
 * beacon at all. The session it owns sends at most once per hide (`MediaAttentionSession.flush`) and
 * once more on unmount.
 */
export class MediaAttentionTracker {
    public static readonly IDLE_CUTOFF_MS = 30_000;
    public static readonly ACCOUNTING_TICK_MS = 5_000;
    public static readonly VIEWABILITY_RATIO = 0.5;
    /** Media items in an injected body. The element itself is the region (§4 P1's "figure/container"). */
    public static readonly MEDIA_SELECTOR = "img, video, iframe";

    private readonly discussionPublicId: string;
    private session: MediaAttentionSession | null = null;
    private observer: IntersectionObserver | null = null;
    private tickId: ReturnType<typeof setInterval> | null = null;
    private stopUserActivityTracking: (() => void) | null = null;
    private readonly hideListeners: Array<() => void> = [];

    /** Per item: is it viewable right now (clause 3), and has it ever been attentive (P2)? */
    private readonly viewable = new Map<Element, boolean>();
    private readonly everAttentive = new Set<Element>();

    private attentiveMs = 0;
    private firstAttentionMs: number | null = null;
    /** The last accounting point, and whether the page was visible when it was taken. */
    private lastTickAt = 0;
    private visibleAtLastTick = true;
    private stopped = false;

    constructor(discussionPublicId: string) {
        this.discussionPublicId = discussionPublicId;
    }

    /**
     * Begins watching every media item inside `bodyElement`, the node the post's HTML was injected
     * into. Items are collected once: the owning effect is keyed on the body HTML, so a new body means
     * a new tracker, and an item detached by a re-render is reported not intersecting by the observer.
     */
    public start(bodyElement: HTMLElement | null): void {
        if (!this.discussionPublicId || !bodyElement || this.observer || this.stopped) {
            return;
        }
        if (typeof IntersectionObserver === "undefined") {
            return; // an engine without it simply does not report media attention
        }
        const items = Array.from(bodyElement.querySelectorAll(MediaAttentionTracker.MEDIA_SELECTOR));
        if (items.length === 0) {
            return; // a text post: nothing to observe, nothing to send
        }

        this.session = MediaAttentionSession.begin(this.discussionPublicId);
        this.stopUserActivityTracking = startUserActivityTracking();
        this.lastTickAt = Date.now();
        this.visibleAtLastTick = isDocumentVisible();

        this.observer = new IntersectionObserver(
            (entries) => this.onIntersections(entries),
            { threshold: [0, 0.25, 0.5, 0.75, 1] },
        );
        items.forEach((item) => {
            this.viewable.set(item, false);
            this.observer?.observe(item);
        });

        this.tickId = setInterval(() => this.account(), MediaAttentionTracker.ACCOUNTING_TICK_MS);

        if (typeof document !== "undefined") {
            const onVisibilityChange = () => {
                this.account();
                if (!isDocumentVisible()) {
                    this.session?.flush();
                }
            };
            document.addEventListener("visibilitychange", onVisibilityChange);
            this.hideListeners.push(() => document.removeEventListener("visibilitychange", onVisibilityChange));
        }
        if (typeof window !== "undefined") {
            const onPageHide = () => {
                this.account();
                this.session?.flush();
            };
            window.addEventListener("pagehide", onPageHide);
            this.hideListeners.push(() => window.removeEventListener("pagehide", onPageHide));
        }
    }

    /** Final accounting, final send, everything released. Idempotent. */
    public stop(): void {
        if (this.stopped) {
            return;
        }
        if (this.observer) {
            this.account();
        }
        this.stopped = true;
        this.observer?.disconnect();
        this.observer = null;
        if (this.tickId !== null) {
            clearInterval(this.tickId);
            this.tickId = null;
        }
        this.hideListeners.splice(0).forEach((remove) => remove());
        this.stopUserActivityTracking?.();
        this.stopUserActivityTracking = null;
        this.session?.end();
        this.session = null;
    }

    /** Clause 3, from one entry. Exposed so the spec can pin the disjunction on its own. */
    public static isViewable(entry: IntersectionObserverEntry): boolean {
        if (!entry.isIntersecting) {
            return false;
        }
        if (entry.intersectionRatio >= MediaAttentionTracker.VIEWABILITY_RATIO) {
            return true;
        }
        const viewportWidth = entry.rootBounds?.width ?? (typeof window === "undefined" ? 0 : window.innerWidth);
        const viewportHeight = entry.rootBounds?.height ?? (typeof window === "undefined" ? 0 : window.innerHeight);
        const viewportArea = viewportWidth * viewportHeight;
        if (viewportArea <= 0) {
            return false;
        }
        const coveredArea = entry.intersectionRect.width * entry.intersectionRect.height;
        return coveredArea / viewportArea >= MediaAttentionTracker.VIEWABILITY_RATIO;
    }

    private onIntersections(entries: IntersectionObserverEntry[]): void {
        // Credit the interval that just ended under the OLD geometry, then adopt the new one.
        this.account();
        entries.forEach((entry) => {
            if (this.viewable.has(entry.target)) {
                this.viewable.set(entry.target, MediaAttentionTracker.isViewable(entry));
            }
        });
        // A visible, active reader whose item just came into view: mark it seen now rather than at the
        // next tick, so a quick look followed by a navigation still counts as one item seen.
        this.markSeenIfAttentive();
    }

    /** Clauses 1 and 2 hold at this instant. */
    private readerIsAttentive(): boolean {
        return isDocumentVisible() && millisSinceLastUserActivity() < MediaAttentionTracker.IDLE_CUTOFF_MS;
    }

    private markSeenIfAttentive(): void {
        if (!this.readerIsAttentive()) {
            return;
        }
        let changed = false;
        this.viewable.forEach((isViewable, item) => {
            if (isViewable) {
                changed = this.noteAttentive(item) || changed;
            }
        });
        if (changed) {
            this.syncSession();
        }
    }

    /** @return whether this is news (a new item seen, or the first attention of the page instance). */
    private noteAttentive(item: Element): boolean {
        const isNew = !this.everAttentive.has(item);
        this.everAttentive.add(item);
        if (this.firstAttentionMs === null) {
            this.firstAttentionMs = MediaAttentionTracker.millisSinceTimeOrigin();
            return true;
        }
        return isNew;
    }

    private syncSession(): void {
        this.session?.recordAttention({
            attentiveMs: this.attentiveMs,
            itemsSeen: this.everAttentive.size,
            firstAttentionMs: this.firstAttentionMs,
        });
    }

    /**
     * Integrates the interval since the last accounting point. Attentive time is credited only up to
     * the moment the reader went idle, and only while the page was visible (a hidden page's stretch is
     * credited at the visibility change that hid it, with nothing after).
     */
    private account(): void {
        if (!this.observer) {
            return;
        }
        const now = Date.now();
        const elapsed = Math.max(0, now - this.lastTickAt);
        // `visibilitychange` fires AFTER the state flipped, so the interval that just ended is judged
        // by the state it started in: the stretch before a hide is credited, the stretch after is not.
        const wasVisible = this.visibleAtLastTick;
        this.lastTickAt = now;
        this.visibleAtLastTick = isDocumentVisible();
        if (elapsed === 0 || !wasVisible) {
            return;
        }
        // The interval [now - elapsed, now] is attentive until idleAt = lastActivity + cutoff.
        const idleAt = now - millisSinceLastUserActivity() + MediaAttentionTracker.IDLE_CUTOFF_MS;
        const attentiveElapsed = Math.max(0, Math.min(elapsed, idleAt - (now - elapsed)));
        if (attentiveElapsed <= 0) {
            return;
        }
        let credited = false;
        this.viewable.forEach((isViewable, item) => {
            if (!isViewable) {
                return;
            }
            this.attentiveMs += attentiveElapsed;
            this.noteAttentive(item);
            credited = true;
        });
        if (credited) {
            this.syncSession();
        }
    }

    private static millisSinceTimeOrigin(): number {
        if (typeof performance !== "undefined" && typeof performance.now === "function") {
            return Math.round(performance.now());
        }
        return 0;
    }
}
